import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
}

interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    avatarUrl?: string;
  };
  token: string;
}

export function useLogin() {
  return useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<AuthResponse> => {
      return api.post('/auth/login', credentials);
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: async (data: RegisterData): Promise<AuthResponse> => {
      return api.post('/auth/register', data);
    },
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ['auth', 'current-user'],
    queryFn: async (): Promise<AuthResponse['user']> => {
      return api.get('/auth/me');
    },
    retry: false,
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      return api.post('/auth/logout');
    },
  });
}

interface ForgotPasswordData {
  email: string;
}

interface ForgotPasswordResponse {
  message: string;
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (data: ForgotPasswordData): Promise<ForgotPasswordResponse> => {
      return api.post('/auth/forgot-password', data);
    },
  });
}

interface ResetPasswordData {
  token: string;
  password: string;
}

interface ResetPasswordResponse {
  message: string;
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (data: ResetPasswordData): Promise<ResetPasswordResponse> => {
      return api.post('/auth/reset-password', data);
    },
  });
}

interface VerifyEmailData {
  token: string;
}

interface VerifyEmailResponse {
  message: string;
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: async (data: VerifyEmailData): Promise<VerifyEmailResponse> => {
      return api.post('/auth/verify-email', data);
    },
  });
}
